/**
 * Platform Admin User Management - Platform admin interface for managing platform administrator users
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Shield, CheckCircle, Mail, Phone } from 'lucide-react';
import {
  AdminPageLayout,
  StatCard,
  StatGrid,
  Modal,
  FormInput,
  ConfirmDialog,
  Section,
  Button,
  Badge
} from '../../components';
import {
  Listing,
  ListingActionBar,
  ListingFilter,
  ListingItem
} from '../../components/listing/v2';
import {
  DeleteAction,
  CreateAction
} from '../../components/listing/v2/ListingAction';
import { useToast } from '../../contexts/ToastContext';
import apiService from '../../services/api';

const PlatformAdminManagement = () => {
  const { showSuccess, showError } = useToast();

  const [platformAdmins, setPlatformAdmins] = useState([]);
  const [stats, setStats] = useState({
    totalAdmins: 0,
    activeAdmins: 0
  });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: ''
  });

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showRevokeDialog, setShowRevokeDialog] = useState(null);

  // Form states
  const [createForm, setCreateForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    phone: ''
  });
  const [assignForm, setAssignForm] = useState({
    email: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);

      const [adminsData, statsData] = await Promise.all([
        apiService.getPlatformAdmins(),
        apiService.getPlatformAdminStats()
      ]);

      setPlatformAdmins(adminsData.admins || []);
      setStats(statsData || { totalAdmins: 0, activeAdmins: 0 });
    } catch (err) {
      console.error('Error loading platform admin data:', err);
      showError('Failed to load platform admin data');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const handleCreateAdmin = async () => {
    try {
      setSubmitting(true);
      await apiService.createPlatformAdmin(createForm);
      showSuccess('Platform admin created successfully');
      setShowCreateModal(false);
      setCreateForm({
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        phone: ''
      });
      await loadData();
    } catch (err) {
      console.error('Error creating platform admin:', err);
      showError(`Failed to create platform admin: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleAssignRole = async () => {
    try {
      setSubmitting(true);
      // For now, we'll assume the email corresponds to a user ID
      // In a real implementation, you might need a user search endpoint
      showError('Assign role functionality requires user search endpoint');
      return;
    } catch (err) {
      console.error('Error assigning platform admin role:', err);
      showError(`Failed to assign platform admin role: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevokeRole = async () => {
    if (!showRevokeDialog) return;

    try {
      setSubmitting(true);
      await apiService.revokePlatformAdmin(showRevokeDialog.id);
      showSuccess('Platform admin role revoked successfully');
      setShowRevokeDialog(null);
      await loadData();
    } catch (err) {
      console.error('Error revoking platform admin role:', err);
      showError(`Failed to revoke platform admin role: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const resetCreateForm = () => {
    setCreateForm({
      first_name: '',
      last_name: '',
      email: '',
      password: '',
      phone: ''
    });
  };

  const resetAssignForm = () => {
    setAssignForm({ email: '' });
  };

  const filteredAdmins = platformAdmins.filter(admin => {
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      if (!`${admin.first_name} ${admin.last_name}`.toLowerCase().includes(searchTerm) &&
        !admin.email?.toLowerCase().includes(searchTerm)) {
        return false;
      }
    }
    return true;
  });

  const filterBar = (
    <ListingFilter
      searchValue={filters.search}
      onSearchChange={(val) => handleFilterChange('search', val)}
      searchPlaceholder="Search by name or email..."
      filters={[]}
      activeFilters={{}}
      onFilterChange={() => { }}
      onClearFilters={() => setFilters({ search: '' })}
    />
  );

  const headerActions = (
    <ListingActionBar
      rightSection={
        <CreateAction
          onClick={() => setShowCreateModal(true)}
          label="New Admin"
        />
      }
    />
  );

  return (
    <AdminPageLayout
      title="PLATFORM ADMINISTRATION"
      subtitle="Governing the core infrastructure nodes"
      maxWidth="1400px"
    >
      {/* 1. Statistics Summary */}
      <Section title="Ecosystem Authority">
        <StatGrid>
          <StatCard
            number={stats.totalAdmins}
            label="Authorized Nodes"
            icon={<Shield size={24} />}
            detail="Total platform guardians"
          />
          <StatCard
            number={stats.activeAdmins}
            label="Active Authorities"
            icon={<CheckCircle size={24} />}
            variant="success"
          />
        </StatGrid>
      </Section>

      <Section title="Platform Admins">
        <Listing
          loading={loading}
          items={filteredAdmins}
          actions={headerActions}
          filters={filterBar}
          variant="list"
          renderItem={(admin) => (
            <ListingItem
              key={admin.id}
              title={`${admin.first_name} ${admin.last_name}`}
              subtitle={admin.email}
              icon={<Shield size={32} />}
              iconVariant="primary"
              actions={
                <DeleteAction
                  size="xs"
                  onClick={() => setShowRevokeDialog(admin)}
                  title="Revoke Permissions"
                />
              }
            >
              <div style={{ display: 'flex', gap: '1rem', marginTop: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <Badge variant="primary" size="sm">PLATFORM ADMIN</Badge>

                {admin.phone && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--color-text-tertiary)', fontSize: '0.85rem' }}>
                    <Phone size={14} /> {admin.phone}
                  </div>
                )}

                {admin.last_login && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--color-text-tertiary)', fontSize: '0.85rem' }}>
                    <CheckCircle size={14} />
                    Last Active: {new Date(admin.last_login).toLocaleDateString()}
                  </div>
                )}
              </div>
            </ListingItem>
          )}
          emptyMessage="No platform administrators identified"
        />
      </Section>

      {/* Create Platform Admin Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          resetCreateForm();
        }}
        title="Provision Platform Authority"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <FormInput
              label="First Name"
              name="first_name"
              value={createForm.first_name}
              onChange={(e) => setCreateForm(prev => ({ ...prev, first_name: e.target.value }))}
              required
            />
            <FormInput
              label="Last Name"
              name="last_name"
              value={createForm.last_name}
              onChange={(e) => setCreateForm(prev => ({ ...prev, last_name: e.target.value }))}
              required
            />
          </div>
          <FormInput
            label="Email Address"
            type="email"
            name="email"
            value={createForm.email}
            onChange={(e) => setCreateForm(prev => ({ ...prev, email: e.target.value }))}
            required
            icon={<Mail size={18} />}
          />
          <FormInput
            label="Contact phone"
            name="phone"
            value={createForm.phone}
            onChange={(e) => setCreateForm(prev => ({ ...prev, phone: e.target.value }))}
            icon={<Phone size={18} />}
          />
          <FormInput
            label="Strategic Password"
            type="password"
            name="password"
            value={createForm.password}
            onChange={(e) => setCreateForm(prev => ({ ...prev, password: e.target.value }))}
            required
            help="One-time password for initialization"
          />
        </div>
        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '2.5rem' }}>
          <Button
            onClick={() => {
              setShowCreateModal(false);
              resetCreateForm();
            }}
            variant="outline"
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateAdmin}
            disabled={!createForm.first_name || !createForm.last_name || !createForm.email || !createForm.password || submitting}
            loading={submitting}
            variant="save"
          >
            Initialise Admin
          </Button>
        </div>
      </Modal>

      {/* Assign Role Modal */}
      <Modal
        isOpen={showAssignModal}
        onClose={() => {
          setShowAssignModal(false);
          resetAssignForm();
        }}
        title="Assign Platform Admin"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          <FormInput
            label="Email"
            type="email"
            name="email"
            value={assignForm.email}
            onChange={(e) => setAssignForm(prev => ({ ...prev, email: e.target.value }))}
            required
            help="Escalate an existing user to platform authority"
            icon={<Mail size={18} />}
          />
        </div>
        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '2.5rem' }}>
          <Button
            onClick={() => {
              setShowAssignModal(false);
              resetAssignForm();
            }}
            variant="outline"
          >
            Cancel
          </Button>
          <Button
            onClick={handleAssignRole}
            disabled={!assignForm.email || submitting}
            loading={submitting}
            variant="primary"
          >
            Escalate Role
          </Button>
        </div>
      </Modal>

      {/* Revoke Role Confirmation Dialog */}
      {showRevokeDialog && (
        <ConfirmDialog
          isOpen={true}
          onClose={() => setShowRevokeDialog(null)}
          onConfirm={handleRevokeRole}
          title="Revoke Strategic Authority"
          variant="danger"
          loading={submitting}
          confirmText="Confirm Revocation"
        >
          <p>
            Are you sure you want to revoke platform authority for <strong>{showRevokeDialog.first_name} {showRevokeDialog.last_name}</strong>?
          </p>
          <p style={{ marginTop: '0.5rem', opacity: 0.7, fontSize: '0.9rem' }}>
            This will immediately suspend all administrative access to the platform core.
          </p>
        </ConfirmDialog>
      )}
    </AdminPageLayout>
  );
};

export default PlatformAdminManagement;